import { z } from 'zod';
import {
  HexColourSchema,
  I18nStringSchema,
  LocaleSchema,
  SlugSchema,
  TimestampSchema,
} from './primitives.js';

/**
 * Platform brand — the SaaS itself, configurable end-to-end.
 *
 * Multi-tenant from day one (ADR-0017): the collection `platformBrands/{brandId}`
 * holds one document per brand. A single Firebase project can host many platform brands;
 * each operator tenant + agent carries `platformBrandId` to bind them to a brand.
 *
 * Resolution: Kilat-branded surfaces (dashboard, agent-pwa, marketing, find) resolve
 * which brand they serve from the request hostname. The customer-pwa resolves brand
 * transitively through its tenant.
 *
 * Edited by: super-admin scoped to that brand (`superadmin: { [brandId]: true }`).
 * Read by: every Kilat-branded surface via `usePlatformBrand()`.
 *
 * Default seed: `packages/config-seed/src/platform-brand.ts` ships one brand (`kilat`).
 */

export const PlatformDomainsSchema = z.object({
  marketing: z.string().describe('e.g., "kilatcarwash.net"'),
  dashboard: z.string().describe('e.g., "dashboard.kilatcarwash.net"'),
  agent: z.string().describe('e.g., "agent.kilatcarwash.net"'),
  find: z.string().describe('e.g., "find.kilatcarwash.net"'),
  /**
   * INBOUND ONLY — never build an outbound URL from this.
   *
   * The white-label app is served at `{marketing}/{slug}` (ADR-0039/0040). This
   * suffix survives solely to RECOGNISE a request that arrives on a legacy
   * subdomain or a Phase-2 custom domain, so brand resolution still works for
   * hosts that were minted before the move.
   *
   * Six call sites once built `https://{slug}.{this}` by hand, and every one of
   * them kept doing so after delivery changed — producing well-formed URLs that
   * resolved to nothing, including the QR code operators print and laminate.
   * Use `customerAppUrl(brand.domains, slug)` from `@kilat/shared` instead; it
   * is the only supported way to construct a tenant's address.
   */
  customerSubdomainSuffix: z
    .string()
    .describe(
      'Inbound host matching only, e.g. "app.kilatcarwash.net". Build outbound URLs with customerAppUrl().',
    ),
  /** Additional hostnames also mapped to this brand (custom domains, mirrors). */
  aliases: z.array(z.string()).default([]),
});
export type PlatformDomains = z.infer<typeof PlatformDomainsSchema>;

export const PlatformColoursSchema = z.object({
  primary: HexColourSchema,
  primaryFg: HexColourSchema,
  secondary: HexColourSchema,
  secondaryFg: HexColourSchema,
  accent: HexColourSchema,
  accentFg: HexColourSchema,
  surface: HexColourSchema,
  surfaceFg: HexColourSchema,
});
export type PlatformColours = z.infer<typeof PlatformColoursSchema>;

export const PlatformTypographySchema = z.object({
  font: z.string().describe('"system" or a Google Font family name'),
  weight: z.enum(['regular', 'medium', 'semibold']).default('regular'),
});

export const PlatformContactSchema = z.object({
  email: z.string().email(),
  phone: z.string().optional(),
  whatsapp: z.string().optional(),
  addressLine: z.string().optional(),
});

export const PlatformSocialSchema = z.object({
  twitter: z.string().url().optional(),
  instagram: z.string().url().optional(),
  facebook: z.string().url().optional(),
  linkedin: z.string().url().optional(),
  tiktok: z.string().url().optional(),
});

export const PlatformLegalSchema = z.object({
  companyName: z.string().describe('Legal entity, e.g., "Kilat Sdn Bhd"'),
  companyRegistration: z.string().optional(),
  taxId: z.string().optional(),
  address: z.string().optional(),
});

export const PlatformCopySchema = z.object({
  heroHeadline: I18nStringSchema,
  heroSubheadline: I18nStringSchema,
  pitchOneLiner: I18nStringSchema,
  footerByline: I18nStringSchema,
  networkAttributionFooter: I18nStringSchema.describe(
    'Template like "Part of the {{network}}" — {{network}} substitutes networkName',
  ),
  networkAttributionAbout: I18nStringSchema,
});

export const PlatformLinksSchema = z.object({
  tos: z.string().default('/legal/tos'),
  privacy: z.string().default('/legal/privacy'),
  agentAgreement: z.string().default('/legal/agent-agreement'),
  support: z.string().default('mailto:support@kilatcarwash.net'),
  statusPage: z.string().url().optional(),
});

/**
 * Panel-configurable launch capability flags (ADR-0023).
 *
 * A small map of reversible, super-admin-writable switches that let the owner
 * narrow launch scope without deleting code. Read through the resolved
 * accessors in `src/platform/capabilities.ts` (never reach into the shape
 * directly) so a missing field always falls back to the safe default.
 *
 * Both defaults are chosen so that *enabling* this feature changes NOTHING at
 * runtime today — see each field's `.describe()`.
 */
export const PlatformCapabilitiesSchema = z
  .object({
    recruiterChannel: z
      .boolean()
      .default(true)
      .describe(
        'ADR-0023: whether the sales-agent recruiter layer is enabled (agent-pwa /recruiter/* + nav, marketing /become-a-recruiter[/signup], recruiter commission split). Default TRUE preserves current behaviour; the owner flips it OFF via the super-admin panel to defer the recruiter channel for launch (the 2026-07-05 audit recommendation).',
      ),
    marketplaceWashPayments: z
      .boolean()
      .default(true)
      .describe(
        'ADR-0023: whether Kilat-collected (merchant-of-record) wash payments via toyyibPay are enabled. Default TRUE because this flow is LIVE today (createToyyibPayBill/toyyibPayWebhook) — defaulting it off would break the live pay path. The owner flips it OFF via the panel to defer MOR, dropping wash collection to cash / operator-own-QR.',
      ),
    leaderboard: z
      .boolean()
      .default(true)
      .describe(
        'ADR-0023: whether the weekly agent leaderboard is enabled. Default TRUE — the `leaderboardOnSignup` trigger maintains the per-brand rollup at `leaderboards/{brandId}/weeks/{isoWeekKey}` and the agent-pwa shows the tile. The owner flips it OFF via the panel to pause ranking (the trigger no-ops; existing week docs remain readable).',
      ),
  })
  .default({ recruiterChannel: true, marketplaceWashPayments: true, leaderboard: true })
  .describe('ADR-0023 panel-configurable launch capability flags.');
export type PlatformCapabilities = z.infer<typeof PlatformCapabilitiesSchema>;

export const PlatformBrandSchema = z.object({
  /** Short slug; doc id. e.g., "kilat", "washhub". */
  id: SlugSchema,
  /** Whether new operator/agent signups against this brand are accepted. */
  active: z.boolean().default(true),

  name: z.string().min(1).max(40),
  shortName: z.string().min(1).max(12),
  networkName: z.string().min(1).max(60).describe('Used in customer-pwa attribution'),
  tagline: I18nStringSchema,

  domains: PlatformDomainsSchema,

  logoUrl: z.string().url(),
  iconUrl: z.string().url(),
  monochromeIconUrl: z.string().url(),

  colours: PlatformColoursSchema,
  typography: PlatformTypographySchema,
  contact: PlatformContactSchema,
  social: PlatformSocialSchema,
  legal: PlatformLegalSchema,
  copy: PlatformCopySchema,
  links: PlatformLinksSchema,

  supportedLocales: z.array(LocaleSchema).min(1),
  defaultLocale: LocaleSchema,

  /**
   * Launch capability flags (ADR-0023). Has an object-level `.default(...)`, so
   * existing brand docs written before this field still parse — they resolve to
   * the behaviour-neutral defaults (everything on).
   */
  capabilities: PlatformCapabilitiesSchema,

  /** Bumped on every save; used in ETags for cache busting. */
  version: z.number().int().nonnegative(),

  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type PlatformBrand = z.infer<typeof PlatformBrandSchema>;
